function daysAgo(days, hour = 10, minute = 0) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(hour, minute, 0, 0);
  return d;
}

function normalizeAccountType(type) {
  return String(type || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ');
}

function roundCurrency(value) {
  return Number(Math.max(0, value).toFixed(2));
}

function accountBalance(account) {
  const available = Number(account?.available);
  const balance = Number(account?.balance);
  if (Number.isFinite(available) && available > 0) return available;
  if (Number.isFinite(balance) && balance > 0) return balance;
  return 0;
}

/**
 * Relative size of each sample vs the account balance.
 * Fixed tiny retail amounts are replaced by balance-aware sizing.
 */
const ACCOUNT_SAMPLE_TEMPLATES = {
  checking: [
    { type: 'credit', description: 'Direct Deposit - Employer Payroll', fraction: 0.045, category: 'Income', daysAgo: 2, min: 1200, max: 25000 },
    { type: 'debit', description: 'Monthly Rent / Housing Payment', fraction: 0.018, category: 'Housing', daysAgo: 5, min: 800, max: 12000 },
    { type: 'debit', description: 'Grocery Purchase', fraction: 0.0012, category: 'Shopping', daysAgo: 3, min: 40, max: 800 },
    { type: 'debit', description: 'Fuel / Transport', fraction: 0.00045, category: 'Transport', daysAgo: 4, min: 25, max: 350 },
    { type: 'debit', description: 'Subscription Services', fraction: 0.00008, category: 'Subscriptions', daysAgo: 8, min: 10, max: 120 },
    { type: 'debit', description: 'ATM Withdrawal', fraction: 0.00035, category: 'Withdrawal', daysAgo: 12, min: 40, max: 500 },
  ],
  savings: [
    { type: 'credit', description: 'Transfer from Checking', fraction: 0.02, category: 'Transfer', daysAgo: 3, min: 200, max: 100000 },
    { type: 'credit', description: 'Monthly Savings Interest', fraction: 0.0022, category: 'Interest', daysAgo: 1, min: 5, max: 50000 },
    { type: 'credit', description: 'Automatic Savings Deposit', fraction: 0.008, category: 'Transfer', daysAgo: 10, min: 100, max: 50000 },
    { type: 'debit', description: 'Transfer to Checking', fraction: 0.006, category: 'Transfer', daysAgo: 18, min: 100, max: 75000 },
    { type: 'credit', description: 'Bonus Savings Credit', fraction: 0.0004, category: 'Interest', daysAgo: 22, min: 10, max: 10000 },
  ],
  investment: [
    { type: 'credit', description: 'Monthly Portfolio Return', fraction: 0.006, category: 'Investment Returns', daysAgo: 2, min: 50, max: 500000 },
    { type: 'credit', description: 'Dividend Distribution', fraction: 0.0028, category: 'Investment Returns', daysAgo: 9, min: 25, max: 250000 },
    { type: 'credit', description: 'Equity Fund Distribution', fraction: 0.0035, category: 'Investment Returns', daysAgo: 16, min: 40, max: 300000 },
    { type: 'debit', description: 'Portfolio Management Fee', fraction: 0.00025, category: 'Fees', daysAgo: 20, min: 15, max: 25000 },
    { type: 'credit', description: 'Reinvested Dividend - Claireoaks Growth Fund', fraction: 0.0018, category: 'Investment Returns', daysAgo: 27, min: 20, max: 200000 },
    { type: 'credit', description: 'Bond Coupon Payment', fraction: 0.0022, category: 'Investment Returns', daysAgo: 12, min: 30, max: 180000 },
  ],
  inheritance: [
    { type: 'credit', description: 'Estate Distribution Deposit', fraction: 0.12, category: 'Inheritance', daysAgo: 4, min: 5000, max: 2000000 },
    { type: 'credit', description: 'Trust Disbursement', fraction: 0.035, category: 'Inheritance', daysAgo: 11, min: 1000, max: 750000 },
    { type: 'debit', description: 'Beneficiary Transfer Out', fraction: 0.008, category: 'Transfer', daysAgo: 15, min: 500, max: 250000 },
    { type: 'credit', description: 'Inheritance Interest Credit', fraction: 0.0015, category: 'Interest', daysAgo: 21, min: 25, max: 80000 },
  ],
  'money market': [
    { type: 'credit', description: 'Money Market Interest', fraction: 0.0035, category: 'Interest', daysAgo: 1, min: 10, max: 80000 },
    { type: 'credit', description: 'Sweep Deposit from Checking', fraction: 0.015, category: 'Transfer', daysAgo: 6, min: 250, max: 200000 },
    { type: 'debit', description: 'Sweep to Checking', fraction: 0.008, category: 'Transfer', daysAgo: 13, min: 200, max: 150000 },
    { type: 'credit', description: 'Money Market Yield Credit', fraction: 0.002, category: 'Interest', daysAgo: 25, min: 8, max: 60000 },
  ],
  'business checking': [
    { type: 'credit', description: 'Client Payment - Invoice Settlement', fraction: 0.055, category: 'Business Income', daysAgo: 2, min: 1500, max: 500000 },
    { type: 'debit', description: 'Vendor Payment', fraction: 0.008, category: 'Business Expense', daysAgo: 5, min: 200, max: 100000 },
    { type: 'debit', description: 'Payroll ACH Batch', fraction: 0.04, category: 'Payroll', daysAgo: 7, min: 2000, max: 400000 },
    { type: 'credit', description: 'Merchant Settlement Deposit', fraction: 0.022, category: 'Business Income', daysAgo: 9, min: 800, max: 250000 },
    { type: 'debit', description: 'Business Insurance Premium', fraction: 0.0015, category: 'Insurance', daysAgo: 17, min: 100, max: 25000 },
  ],
  'business savings': [
    { type: 'credit', description: 'Transfer from Business Checking', fraction: 0.03, category: 'Transfer', daysAgo: 3, min: 500, max: 300000 },
    { type: 'credit', description: 'Business Savings Interest', fraction: 0.0025, category: 'Interest', daysAgo: 1, min: 15, max: 60000 },
    { type: 'debit', description: 'Reserve Transfer Out', fraction: 0.012, category: 'Transfer', daysAgo: 14, min: 250, max: 150000 },
  ],
  trust: [
    { type: 'credit', description: 'Trust Contribution Deposit', fraction: 0.05, category: 'Trust', daysAgo: 5, min: 2000, max: 1000000 },
    { type: 'credit', description: 'Trust Income Distribution', fraction: 0.012, category: 'Trust', daysAgo: 12, min: 400, max: 200000 },
    { type: 'debit', description: 'Trustee Disbursement', fraction: 0.01, category: 'Trust', daysAgo: 19, min: 300, max: 150000 },
    { type: 'credit', description: 'Trust Account Interest', fraction: 0.0018, category: 'Interest', daysAgo: 26, min: 20, max: 75000 },
  ],
  'certificate of deposit': [
    { type: 'credit', description: 'CD Interest Credit', fraction: 0.0038, category: 'Interest', daysAgo: 2, min: 25, max: 100000 },
    { type: 'credit', description: 'CD Principal Deposit', fraction: 0.25, category: 'Deposit', daysAgo: 28, min: 1000, max: 2000000 },
    { type: 'credit', description: 'CD Maturity Interest', fraction: 0.0075, category: 'Interest', daysAgo: 15, min: 40, max: 150000 },
  ],
};

const GENERIC_TEMPLATES = [
  { type: 'credit', description: 'Account Deposit', fraction: 0.02, category: 'Deposit', daysAgo: 4, min: 100, max: 250000 },
  { type: 'debit', description: 'Account Withdrawal', fraction: 0.005, category: 'Withdrawal', daysAgo: 9, min: 50, max: 75000 },
  { type: 'credit', description: 'Interest Credit', fraction: 0.0015, category: 'Interest', daysAgo: 16, min: 5, max: 50000 },
  { type: 'debit', description: 'Service Fee', fraction: 0.00005, category: 'Fees', daysAgo: 22, min: 5, max: 500 },
];

function templatesForAccountType(accountType) {
  const key = normalizeAccountType(accountType);
  if (ACCOUNT_SAMPLE_TEMPLATES[key]) return ACCOUNT_SAMPLE_TEMPLATES[key];

  if (key.includes('invest')) return ACCOUNT_SAMPLE_TEMPLATES.investment;
  if (key.includes('inherit') || key.includes('estate')) return ACCOUNT_SAMPLE_TEMPLATES.inheritance;
  if (key.includes('money market') || key === 'mma') return ACCOUNT_SAMPLE_TEMPLATES['money market'];
  if (key.includes('business') && key.includes('saving')) return ACCOUNT_SAMPLE_TEMPLATES['business savings'];
  if (key.includes('business')) return ACCOUNT_SAMPLE_TEMPLATES['business checking'];
  if (key.includes('trust')) return ACCOUNT_SAMPLE_TEMPLATES.trust;
  if (key.includes('certificate') || key === 'cd' || key.includes(' cd')) {
    return ACCOUNT_SAMPLE_TEMPLATES['certificate of deposit'];
  }
  if (key.includes('saving')) return ACCOUNT_SAMPLE_TEMPLATES.savings;
  if (key.includes('check') || key.includes('current')) return ACCOUNT_SAMPLE_TEMPLATES.checking;

  return GENERIC_TEMPLATES;
}

function scaleAmount(template, balance) {
  const base = Math.max(balance, 500);
  const fraction = Number(template.fraction || 0.01);
  const raw = base * fraction;
  const min = Number(template.min ?? 1);
  let max = Number(template.max ?? Number.MAX_SAFE_INTEGER);
  // High balances: lift retail maxes so a $4M account shows proportional activity
  // (keep small everyday spends near their configured max)
  if (balance >= 250000 && fraction >= 0.005) {
    max = Math.max(max, raw);
  } else if (balance >= 1000000 && fraction >= 0.001) {
    max = Math.max(max, Math.min(raw, balance * 0.02));
  }
  const scaled =
    balance >= 50000 ? Math.max(raw, min) : Math.max(raw, Math.min(min, base * 0.05));
  return roundCurrency(Math.min(scaled, max, base * 0.4));
}

function buildMockFromTemplate(template, account, idPrefix, index) {
  const accountType = String(account?.type || 'Checking').trim() || 'Checking';
  const balance = accountBalance(account);
  return {
    id: `${idPrefix}-${index}`,
    description: template.description,
    amount: scaleAmount(template, balance),
    type: template.type,
    date: daysAgo(template.daysAgo ?? 3, 10 + (index % 6), (index * 7) % 60),
    category: template.category,
    status: 'Completed',
    accountType,
    isMock: true,
  };
}

/**
 * Sample transactions tailored to each account type and scaled to its balance.
 * Investment samples are included unless real investment activity already exists
 * (or `skipInvestment` is forced true).
 */
export function getMockTransactions(accounts = [], options = {}) {
  const { existingTransactions = [] } = options;
  const hasRealInvestmentActivity = (Array.isArray(existingTransactions) ? existingTransactions : []).some(
    (t) => {
      if (t?.isMock) return false;
      const type = String(t.accountType || '').toLowerCase();
      const category = String(t.category || '').toLowerCase();
      return type.includes('invest') || category.includes('investment');
    }
  );
  const skipInvestment =
    options.skipInvestment !== undefined ? options.skipInvestment : hasRealInvestmentActivity;

  const list =
    Array.isArray(accounts) && accounts.length > 0
      ? accounts
      : [{ type: 'Checking', available: 2500, balance: 2500 }];

  const transactions = [];

  list.forEach((account, accountIndex) => {
    if (account?.disabled === true) return;
    const accountType = String(account?.type || 'Checking').trim() || 'Checking';
    const normalized = normalizeAccountType(accountType);
    if (skipInvestment && normalized.includes('invest')) {
      return;
    }

    const templates = templatesForAccountType(accountType);
    templates.forEach((template, index) => {
      transactions.push(
        buildMockFromTemplate(template, account, `mock-${accountIndex}`, index)
      );
    });
  });

  return transactions.sort((a, b) => b.date.getTime() - a.date.getTime());
}

/** Random mock set used when seeding a brand-new user without typed accounts. */
export const generateMockTransactions = (count = 20, accounts = [{ type: 'Checking', available: 2500 }]) => {
  const typed = getMockTransactions(accounts, { skipInvestment: false });
  if (typed.length >= count) {
    return typed.slice(0, count).map(({ isMock, id, ...rest }) => ({
      ...rest,
      date: rest.date instanceof Date ? rest.date : daysAgo(3),
      status: String(rest.status || 'completed').toLowerCase(),
    }));
  }

  const account = accounts[0] || { type: 'Checking', available: 2500 };
  const checkingPool = ACCOUNT_SAMPLE_TEMPLATES.checking;
  return Array.from({ length: count }, (_, index) => {
    const template = checkingPool[index % checkingPool.length];
    return {
      type: template.type,
      description: template.description,
      amount: scaleAmount(template, accountBalance(account)),
      date: daysAgo((index % 28) + 1),
      status: 'completed',
      category: template.category || (template.type === 'credit' ? 'income' : 'expense'),
      accountType: account.type || 'Checking',
    };
  }).sort((a, b) => b.date - a.date);
};

export const initialAccounts = [
  {
    type: 'Checking',
    accountNumber: '1234567890',
    balance: 2500.0,
    routingNumber: '021000021',
  },
  {
    type: 'Savings',
    accountNumber: '0987654321',
    balance: 10000.0,
    routingNumber: '021000021',
  },
];

export const DEFAULT_ACCOUNTS = [
  {
    type: 'Checking',
    accountNumber: '1234567890',
    balance: 2500.0,
    available: 2500.0,
    routingNumber: '021000021',
  },
  {
    type: 'Savings',
    accountNumber: '0987654321',
    balance: 10000.0,
    available: 10000.0,
    routingNumber: '021000021',
  },
];

const INVESTMENT_RETURN_DESCRIPTIONS = [
  'Monthly Investment Return',
  'Portfolio Dividend Credit',
  'Equity Fund Distribution',
  'Bond Coupon Payment',
  'Reinvested Dividend - Claireoaks Growth Fund',
  'Investment Yield Credit',
  'Market Appreciation Credit',
];

const QUARTERLY_RETURN_DESCRIPTIONS = [
  'Quarterly Portfolio Yield',
  'Quarterly Dividend Distribution',
  'QTR Investment Performance Credit',
];

/** Generate monthly investment return credits scaled to the account balance. */
export function generateInvestmentReturnTransactions(availableBalance, months = 12) {
  const balance = Math.max(0, Number(availableBalance) || 0);
  if (balance <= 0) return [];

  const annualRate = 0.072;
  const monthlyRate = annualRate / 12;
  const transactions = [];
  const now = new Date();

  for (let i = 0; i < months; i += 1) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1, 9, 15, 0, 0);
    const monthIndex = monthDate.getMonth() + 1;
    const isQuarterEnd = monthIndex % 3 === 0;

    const variation = 0.88 + ((i * 7 + monthIndex) % 13) * 0.01;
    const monthlyReturn = roundCurrency(balance * monthlyRate * variation);

    if (monthlyReturn > 0) {
      const descriptions = isQuarterEnd ? QUARTERLY_RETURN_DESCRIPTIONS : INVESTMENT_RETURN_DESCRIPTIONS;
      transactions.push({
        type: 'credit',
        amount: monthlyReturn,
        description: descriptions[i % descriptions.length],
        date: monthDate,
        category: 'Investment Returns',
        status: 'Completed',
        accountType: 'Investment',
      });
    }

    if (isQuarterEnd && balance >= 5000) {
      const fee = roundCurrency(Math.min(balance * 0.0008, balance * 0.0025));
      if (fee > 0) {
        const feeDate = new Date(monthDate);
        feeDate.setDate(5);
        feeDate.setHours(11, 0, 0, 0);
        transactions.push({
          type: 'debit',
          amount: fee,
          description: 'Portfolio Management Fee',
          date: feeDate,
          category: 'Investment Returns',
          status: 'Completed',
          accountType: 'Investment',
        });
      }
    }
  }

  return transactions.sort((a, b) => b.date.getTime() - a.date.getTime());
}

/** Visual theme for dashboard surfaces by account type */
export function getAccountTheme(accountType) {
  const key = normalizeAccountType(accountType);

  if (key.includes('invest')) {
    return {
      kind: 'investment',
      label: 'Portfolio value',
      subtitle: 'Investment account',
      accent: 'Returns & distributions',
      heroClass: 'from-[#0c1a24] via-[#163247] to-[#1e4a5c]',
      badgeClass: 'bg-claire-300/20 text-claire-100',
      softClass: 'bg-claire-50 border-claire-200',
      chipActive: 'bg-[#163247] text-white border-[#163247]',
      icon: '📈',
    };
  }
  if (key.includes('inherit') || key.includes('estate')) {
    return {
      kind: 'inheritance',
      label: 'Available balance',
      subtitle: 'Inheritance account',
      accent: 'Estate & beneficiary activity',
      heroClass: 'from-[#2a2118] via-[#3d3226] to-[#5c4a35]',
      badgeClass: 'bg-amber-300/25 text-amber-100',
      softClass: 'bg-amber-50 border-amber-200',
      chipActive: 'bg-[#3d3226] text-white border-[#3d3226]',
      icon: '🏛️',
    };
  }
  if (key.includes('saving') || key.includes('money market') || key.includes('certificate') || key === 'cd') {
    return {
      kind: 'savings',
      label: 'Available balance',
      subtitle: String(accountType || 'Savings'),
      accent: 'Growth & transfers',
      heroClass: 'from-oak-800 via-oak-700 to-claire-800',
      badgeClass: 'bg-oak-200/30 text-oak-100',
      softClass: 'bg-oak-50 border-oak-200',
      chipActive: 'bg-oak-800 text-white border-oak-800',
      icon: '🌱',
    };
  }
  if (key.includes('business')) {
    return {
      kind: 'business',
      label: 'Available balance',
      subtitle: String(accountType || 'Business'),
      accent: 'Operating activity',
      heroClass: 'from-slate-800 via-slate-700 to-claire-900',
      badgeClass: 'bg-slate-200/25 text-slate-100',
      softClass: 'bg-slate-50 border-slate-200',
      chipActive: 'bg-slate-800 text-white border-slate-800',
      icon: '🏢',
    };
  }
  if (key.includes('trust')) {
    return {
      kind: 'trust',
      label: 'Available balance',
      subtitle: 'Trust account',
      accent: 'Trustee activity',
      heroClass: 'from-[#1a2430] via-[#243447] to-[#35506a]',
      badgeClass: 'bg-sky-300/20 text-sky-100',
      softClass: 'bg-sky-50 border-sky-200',
      chipActive: 'bg-[#243447] text-white border-[#243447]',
      icon: '🔏',
    };
  }

  return {
    kind: 'checking',
    label: 'Available balance',
    subtitle: String(accountType || 'Checking'),
    accent: 'Everyday spending',
    heroClass: 'from-primary-600 via-primary-700 to-primary-800',
    badgeClass: 'bg-amber-400/25 text-amber-200',
    softClass: 'bg-primary-50 border-primary-200',
    chipActive: 'bg-primary-700 text-white border-primary-700',
    icon: '💳',
  };
}
